import { Injectable } from '@angular/core';
import {Socket} from "socket.io-client";
import {DefaultEventsMap} from "@socket.io/component-emitter";
import {FileUploadStatus} from "../../state/interface/dtos/file-upload-status.dto";
import ServerFile from "./dtos/server-file.dto";
import {Store} from "@ngrx/store";
import {WebsocketService} from "../websocket.service";
import { selectFileUploadStatuss } from "src/app/state/interface/interface.selector";
import {interfaceActions} from "../../state/interface/interface.actions";
import {ImageCompressService} from "./image-compress.service";

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  fileUploads: { [fileId: string]: FileUploadStatus };

  fileDownloadsById: {[fileId: string]: {
      file: ServerFile,
      downloadCompleted: boolean,
    }} = {}

  constructor(
      private imageCompress: ImageCompressService,
      private store: Store,
  ) {
    this.store.select(selectFileUploadStatuss).subscribe((fileUploads) => {
      this.fileUploads = fileUploads;
    });
  }

  async getServerFileIdForNewFile(file: File, fileSocket: Socket): Promise<string> {
    const size = (file.size / 1000) * 1024;
    const response: ServerFile = await fileSocket.emitWithAck(
        "file_upload_start",
        {
          name: file.name,
          extension: file.type,
          size: size,
        },
    );
    return response.id;
  }


    async getServerFileIdForNewBlob(
        filename: string,
        exifExtension: string,
        fileSize: number, fileSocket: Socket): Promise<string> {
        const size = (fileSize / 1000) * 1024;
        const response: ServerFile = await fileSocket.emitWithAck(
            "file_upload_start",
            {
                name: filename,
                extension: exifExtension,
                size: size,
            },
        );
        return response.id;
    }

    async uploadBlob(
        serverFileId: string,
        filename: string,
        exifExtension: string,
        blob: string,
        fileSocket: Socket,
        resizeImageAndCompress: boolean,
        maxSize: number
    ) {
      let result = blob
      if (resizeImageAndCompress) {
          console.log("Compressing image from: " + (blob).length)
          result = await this.imageCompress.compressBlob(blob, resizeImageAndCompress, maxSize)
          console.log("Compressing image to: " + (blob).length)
      }
      this.store.dispatch(
          interfaceActions.setFileUploadStatus({
              id: serverFileId,
              name: filename,
              extension: exifExtension,
              total: result.length,
              progress: 0,
              completed: false,
          }),
      );
      await this.splitAndUploadFileData(
          serverFileId,
          filename,exifExtension, result.length
          , result, fileSocket
      );
    }

  async uploadFile(serverFileId: string, file: File, fileSocket: Socket, resize: boolean, maxSize: number) {
    var reader = new FileReader();
    const fileData = await new Promise<string | ArrayBuffer>(
        (resolve, reject) => {
          reader.onload = async (e) => {
            let result = e.target.result;
            if (e.target.error) {
              reject(e.target.error);
            }
            if (this.isImage(file)) {
                console.log("Compressing image from: " + (result as string).length)
                result = await this.imageCompress.compressBlob(result as string, resize, maxSize)
                console.log("Compressing image to: " + (result as string).length)
            }
            resolve(result);
          };
          reader.readAsDataURL(file);
        },
    );
    const fileDataString = fileData.toString();
    this.store.dispatch(
        interfaceActions.setFileUploadStatus({
          id: serverFileId,
          name: file.name,
          extension: file.type,
          total: fileDataString.length,
          progress: 0,
          completed: false,
          fileData: fileDataString
        }),
    );
    await this.splitAndUploadFileData(
        serverFileId, file.name, file.type, fileDataString.length,
        fileDataString, fileSocket
    );
  }

  isImage(file: File): boolean {
      return file.type.startsWith('image/');
  }

  async splitAndUploadFileData(
      fileId: string,
      fileName: string,
      fileExtension: string,
      fileSize: number,
      fileDataString: string,
      fileSocket: Socket
  ) {
    const partSize = 5000000; //9mb
    const partCount = Math.ceil(fileDataString.length / partSize);
    const proms = [];
    let countdown = partSize;
    for (let index = 0; index <= partCount; index += 1) {
      const prom = new Promise(async (resolve, reject) => {
        try {
          const part = this.getPartSlice(index, partSize, fileDataString);
          countdown -= part.length;
          await this.uploadPart(fileId, index, part, fileSocket);
          resolve(true);
        } catch (e) {
          reject(e);
        }
      });
      proms.push(prom);
    }
    await Promise.all(proms);
    this.store.dispatch(
        interfaceActions.setFileUploadStatus({
          id: fileId, name: fileName,
          extension: fileExtension,
          total: fileSize, progress: fileSize,
          completed: true,
        }),
    );
  }

  getPartSlice(index, partSize, fileDataString: string) {
    const currentPos = index * partSize;
    let nextPos = (index + 1) * partSize;
    if (nextPos >= fileDataString.length) {
      nextPos = fileDataString.length;
    }
    const part = fileDataString.slice(currentPos, nextPos);
    return part
  }

  async uploadPart(fileId: string, partNumber: number, part: string, fileSocket: Socket) {
    const hash = await this.sha256(part);
    console.log( `[File uploader] Uploading file (${fileId}) part (${partNumber})`, );
    const response: any = await fileSocket.emitWithAck(
        "file_upload_part",
        {
          id: fileId,
          partNumber: partNumber,
          partData: part,
          hash: hash,
        },
    );
    const fileUpload = this.fileUploads[fileId];
    console.log( `[File uploader] Uploading file (${fileId}) part (${partNumber}:${response.part_number}) done, progress (${fileUpload.progress + part.length}/${fileUpload.total})`, );
    this.store.dispatch(
        interfaceActions.setFileUploadStatus({
          id: fileId,
          name: fileUpload.name,
          extension: fileUpload.extension,
          total: fileUpload.total,
          progress: fileUpload.progress + part.length,
          completed: false,
        }),
    );
    return response;
  }


  async sha256(message: string) {
    // encode as UTF-8
    const msgBuffer = new TextEncoder().encode(message);
    // hash the message
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
    // convert ArrayBuffer to Array
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    // convert bytes to hex string
    const hashHex = hashArray
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    return hashHex;
  }
}
